import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBigUp, CheckCircle2, MessageSquare, Plus, Sparkles, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { SearchBox, Tabs, Paragraphs } from "@/components/ContentParts";
import { inputCls } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/lso";

const STATUS_COLOR = { open: "#F5A623", answered: "var(--accent)", closed: "#888" };
const FILTERS = [{ key: "all", label: "ALL" }, { key: "open", label: "OPEN" }, { key: "answered", label: "ANSWERED" }, { key: "mine", label: "MINE" }];

const NewQuestion = ({ categories, onCreated, onCancel }) => {
  const [f, setF] = useState({ title: "", body: "", category: categories[0] || "Other", laser_model: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    if (f.title.trim().length < 5) return setErr("Title must be at least 5 characters.");
    if (f.body.trim().length < 10) return setErr("Add a bit more detail (10+ characters).");
    setBusy(true); setErr(null);
    try { onCreated(await api("/community/questions", { method: "POST", body: { ...f, laser_model: f.laser_model || null } })); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Card className="space-y-3" data-testid="new-question-form">
      <input data-testid="question-title" className={inputCls} placeholder="Question title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <textarea data-testid="question-body" rows={4} className={inputCls} placeholder="Describe the job, substrate, what you've tried…" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <select data-testid="question-category" className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{categories.map((c) => <option key={c}>{c}</option>)}</select>
        <select data-testid="question-laser" className={inputCls} value={f.laser_model} onChange={(e) => setF({ ...f, laser_model: e.target.value })}><option value="">Laser (optional)</option>{["300W", "500W", "1000W", "2000W"].map((m) => <option key={m}>{m}</option>)}</select>
      </div>
      <p className="text-xs text-neutral-500">The ALR AI posts a first answer right away — other franchisees can add theirs.</p>
      <ErrorNote>{err}</ErrorNote>
      <div className="grid grid-cols-2 gap-2"><Btn variant="outline" onClick={onCancel} data-testid="question-cancel">Cancel</Btn><Btn busy={busy} onClick={submit} data-testid="question-submit">Post question</Btn></div>
    </Card>
  );
};

export function Community() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [composing, setComposing] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (filter === "open" || filter === "answered") qs.set("status", filter);
    if (filter === "mine") qs.set("mine", "true");
    if (q.trim()) qs.set("q", q.trim());
    api(`/community/questions${qs.toString() ? `?${qs}` : ""}`).then(setList).catch((e) => setErr(e.message));
  }, [filter, q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api("/community/categories").then(setCategories).catch(() => setCategories(["Other"])); }, []);

  return (
    <PortalPage eyebrow="FRANCHISEE COMMUNITY" title="Ask the network" testId="community-page"
      actions={!composing && <button onClick={() => setComposing(true)} data-testid="ask-question-btn" className="flex h-11 items-center gap-2 rounded-xl bg-[var(--accent)] px-3 text-xs font-extrabold text-black"><Plus size={16} /> Ask</button>}>
      {composing && <NewQuestion categories={categories.length ? categories : ["Other"]} onCancel={() => setComposing(false)} onCreated={(qn) => { setComposing(false); navigate(`/community/${qn.id}`); }} />}
      <SearchBox value={q} onChange={setQ} placeholder="Search questions…" testId="community-search" />
      <Tabs tabs={FILTERS} active={filter} onChange={setFilter} />
      <ErrorNote>{err}</ErrorNote>
      {!list && !err && <Spinner />}
      {list?.length === 0 && <p className="text-sm text-neutral-400" data-testid="community-empty">No questions yet.</p>}
      {list?.map((qn) => (
        <button key={qn.id} onClick={() => navigate(`/community/${qn.id}`)} data-testid={`question-${qn.id}`} className="flex w-full gap-4 rounded-2xl border border-[#333] bg-[#1A1A1A] p-4 text-left hover:border-[var(--accent)]">
          <span className="flex w-10 shrink-0 flex-col items-center text-neutral-400"><ArrowBigUp size={20} /><span className="text-sm font-bold">{qn.upvotes}</span></span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold">{qn.title}</span>
            <span className="mt-1 line-clamp-2 block text-sm text-neutral-400">{qn.body}</span>
            <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500">
              <span className="font-extrabold" style={{ color: STATUS_COLOR[qn.status] }}>{qn.status.toUpperCase()}</span>
              <span>{qn.category}</span>{qn.laser_model && <span>{qn.laser_model}</span>}
              <span className="flex items-center gap-1"><MessageSquare size={12} />{qn.replies.length}</span>
              <span>{qn.asker_name} · {fmtDate(qn.created_at)}</span>
            </span>
          </span>
        </button>
      ))}
    </PortalPage>
  );
}

export function CommunityQuestion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [qn, setQn] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let tries = 0;
    let timer;
    const fetchQ = () => api(`/community/questions/${id}`).then((q) => {
      setQn(q);
      if (!q.replies.length && tries++ < 8) timer = setTimeout(fetchQ, 4000);
    }).catch((e) => setErr(e.message));
    fetchQ();
    return () => clearTimeout(timer);
  }, [id]);
  const act = async (path, opts = { method: "POST" }) => { try { setQn(await api(path, opts)); } catch (e) { toast.error(e.message); } };
  const send = async () => {
    if (reply.trim().length < 5) return toast.error("Replies need at least 5 characters.");
    setBusy(true);
    try { setQn(await api(`/community/questions/${id}/replies`, { method: "POST", body: { body: reply.trim() } })); setReply(""); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this question?")) return;
    try { await api(`/community/questions/${id}`, { method: "DELETE" }); navigate("/community"); } catch (e) { toast.error(e.message); }
  };
  const mine = qn && (qn.asker_id === user.id || user.role === "admin");

  return (
    <PortalPage eyebrow={qn ? `${qn.category}${qn.laser_model ? ` · ${qn.laser_model}` : ""}` : "COMMUNITY"} title={qn?.title || "Question"} back="/community" backLabel="Community" testId="question-page"
      actions={mine && <button onClick={remove} data-testid="question-delete" className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#333] text-neutral-400 hover:text-[#FF3333]"><Trash2 size={18} /></button>}>
      <ErrorNote>{err}</ErrorNote>
      {!qn && !err && <Spinner />}
      {qn && (
        <>
          <Card>
            <div className="text-[15px] leading-7 text-neutral-200"><Paragraphs text={qn.body} /></div>
            <div className="mt-3 flex items-center justify-between text-xs text-neutral-500">
              <span>{qn.asker_name} · {fmtDate(qn.created_at)}</span>
              <button onClick={() => act(`/community/questions/${id}/vote`)} data-testid="question-vote" className={`flex items-center gap-1 rounded-lg border px-2 py-1 font-bold ${qn.upvoted_by.includes(user.id) ? "border-[var(--accent)] text-[var(--accent)]" : "border-[#333]"}`}><ArrowBigUp size={16} />{qn.upvotes}</button>
            </div>
          </Card>
          <h2 className="pt-2 text-[11px] font-bold tracking-[0.3em] text-neutral-400">{qn.replies.length} ANSWER{qn.replies.length === 1 ? "" : "S"}</h2>
          {qn.replies.map((r) => (
            <Card key={r.id} style={r.is_accepted ? { borderColor: "var(--accent)" } : undefined} data-testid={`reply-${r.id}`}>
              <p className="mb-2 flex items-center gap-2 text-xs font-bold">
                {r.author_role === "ai" ? <><Sparkles size={14} className="text-[var(--accent)]" /> ALR AI</> : <>{r.author_name}{r.author_role === "admin" && <span className="rounded bg-[var(--accent)] px-1.5 text-[9px] text-black">ALR</span>}</>}
                {r.is_accepted && <span className="flex items-center gap-1 text-[var(--accent)]"><CheckCircle2 size={14} /> Accepted</span>}
              </p>
              <div className="text-[15px] leading-7 text-neutral-200"><Paragraphs text={r.body} /></div>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <button onClick={() => act(`/community/questions/${id}/replies/${r.id}/vote`)} data-testid={`reply-vote-${r.id}`} className={`flex items-center gap-1 rounded-lg border px-2 py-1 font-bold ${r.upvoted_by.includes(user.id) ? "border-[var(--accent)] text-[var(--accent)]" : "border-[#333] text-neutral-400"}`}><ArrowBigUp size={14} />{r.upvotes}</button>
                {mine && !r.is_accepted && <button onClick={() => act(`/community/questions/${id}/replies/${r.id}/accept`)} data-testid={`reply-accept-${r.id}`} className="rounded-lg border border-[#333] px-2 py-1 font-bold text-neutral-400 hover:text-[var(--accent)]">Accept answer</button>}
              </div>
            </Card>
          ))}
          <Card className="space-y-3">
            <textarea data-testid="reply-input" rows={3} className={inputCls} placeholder="Share what worked for you…" value={reply} onChange={(e) => setReply(e.target.value)} />
            <Btn busy={busy} onClick={send} data-testid="reply-submit"><Send size={14} /> Post answer</Btn>
          </Card>
        </>
      )}
    </PortalPage>
  );
}
